/**
 * Validating a STORED `user_orgs.role` string into an RBAC `GlobalRole`.
 *
 * The column holds exactly the four `GlobalRole` values (see the schema:
 * packages/database/src/schema/d1/operational.ts — `// client, admin, agent,
 * supervisor`, and `userRoleSchema` in @aica/api-types, which is the same four).
 * There is no 'owner' row: the platform super-admin is `users.is_owner`, a flag
 * on a different table, and it is NOT a role.
 *
 * So this is not a mapping — it is a GUARD. It exists because the column is a
 * bare `text` with no CHECK constraint, so what comes back is an untrusted
 * string that TypeScript is happy to let you write `role as GlobalRole` over.
 * That cast is the thing to avoid: it turns a corrupted value into an
 * apparently-valid role, and every permission check then denies it silently.
 * Call `toGlobalRole` and handle `null` visibly instead.
 */
import type { GlobalRole } from './types';

/**
 * The recognized roles, as a total map over `GlobalRole`.
 *
 * Typed `Record<GlobalRole, true>` on purpose: adding a role to `GlobalRole`
 * without adding it here is a compile error, so the guard cannot silently start
 * rejecting a role the rest of the system accepts.
 */
const GLOBAL_ROLES: Record<GlobalRole, true> = {
  client: true,
  admin: true,
  agent: true,
  supervisor: true,
};

/** Whether a raw `user_orgs.role` string is a role the platform recognizes. */
export function isGlobalRole(role: string): role is GlobalRole {
  // `hasOwnProperty.call` rather than `Object.hasOwn` (this module is bundled
  // into the browser app, whose lib target predates it) and rather than `in`
  // (which walks the prototype chain, so a stored role of `"constructor"` would
  // pass the guard — precisely the hole a fail-closed check must not have).
  return Object.prototype.hasOwnProperty.call(GLOBAL_ROLES, role);
}

/**
 * Resolve a stored `user_orgs.role` to a `GlobalRole`, or `null` if it is not
 * one.
 *
 * Never returns a default role. Defaulting would grant access on the strength
 * of a corrupted column; callers must treat `null` as "deny, and say so in the
 * logs". Silence is how an empty recipient list went unnoticed across 213
 * notifications.
 */
export function toGlobalRole(role: string): GlobalRole | null {
  return isGlobalRole(role) ? role : null;
}
